import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { endpoints, type ProjectCite, type ProjectInsights as Insights, type ProjectPoint } from "../lib/api";
import { clock } from "./Cited";

/* A project's Overview and Timeline (src/minutes/project.py): written by AI
 * from every ready meeting of the project, again whenever a meeting is added,
 * removed or rewritten. Every point says where it came from: a meeting tag
 * opens the meeting, a time opens it at that moment (MeetingDetail's ?t=). */

type Mode = "meeting" | "topic";
const MODE_KEY = "project-timeline-mode";

function useInsights(projectId: number) {
  return useQuery({
    queryKey: ["project-insights", projectId],
    queryFn: () => endpoints.projectInsights(projectId),
    // Refresh while it's being written, or while meetings are still on their way.
    refetchInterval: (query) => {
      const data = query.state.data;
      return data && (data.status === "queued" || data.status === "running" || data.pending > 0) ? 10_000 : false;
    },
  });
}

/** "Updated 5 Oct, 4:10 PM · Update now", or what's happening instead. */
function StatusLine({ projectId, insights, csrfToken }: { projectId: number; insights: Insights; csrfToken: string }) {
  const queryClient = useQueryClient();
  const update = useMutation({
    mutationFn: () => endpoints.updateProjectInsights(projectId, csrfToken),
    onSuccess: (next) => queryClient.setQueryData(["project-insights", projectId], next),
  });
  const writing = insights.status === "queued" || insights.status === "running" || update.isPending;
  return (
    <div className="insights-status">
      <span>
        {writing
          ? "Updating from the project's meetings…"
          : insights.written_at
            ? `Written from ${insights.meetings_used.length} meeting${insights.meetings_used.length === 1 ? "" : "s"} · updated ${when(insights.written_at)}`
            : "Not written yet"}
        {insights.pending > 0 && ` · ${insights.pending} meeting${insights.pending === 1 ? " is" : "s are"} still being processed — included when ready`}
      </span>
      <button className="btn-quiet" disabled={writing || !csrfToken} onClick={() => update.mutate()}>
        {writing ? "Updating…" : "Update now"}
      </button>
      {(update.error || insights.status === "failed") && (
        <div className="error">
          {update.error ? (update.error as Error).message : `The last update failed: ${insights.error ?? "no reason given"}`}
        </div>
      )}
    </div>
  );
}

/** Where a point comes from: each meeting once, by its date — opens it. The
 * Overview shows meetings only, not the moments in them; the Timeline keeps
 * its times. */
function Cites({ cites }: { cites: ProjectCite[] }) {
  const meetings = new Map(cites.map((cite) => [cite.session_id, cite]));
  return (
    <>
      {[...meetings.values()].map((cite) => (
        <span key={cite.session_id} className="insight-cite">
          <Link className="insight-meeting" to={`/meetings/${cite.session_id}`} title={cite.title}>
            {shortDate(cite.date)}
          </Link>
        </span>
      ))}
    </>
  );
}

function Point({ point }: { point: ProjectPoint }) {
  return <>{point.text}<Cites cites={point.cites} /></>;
}

function Empty({ insights }: { insights: Insights }) {
  if (insights.meetings === 0) return <p className="doc-empty">Add meetings to the project, and its overview and timeline are written from them.</p>;
  if (insights.status === "queued" || insights.status === "running") {
    return (
      <>
        <p className="doc-empty">Reading the project's meetings — a minute or two. The page updates by itself.</p>
        <div className="doc-progress" aria-hidden />
      </>
    );
  }
  return <p className="doc-empty">Nothing written yet. Press Update now to write it from the project's meetings.</p>;
}

export function ProjectOverview({ projectId, csrfToken }: { projectId: number; csrfToken: string }) {
  const { data, error, isLoading } = useInsights(projectId);
  if (error) return <div className="error">{(error as Error).message}</div>;
  if (isLoading || !data) return <p className="doc-empty">Loading…</p>;
  const overview = data.overview;
  return (
    <section className="insights">
      <StatusLine projectId={projectId} insights={data} csrfToken={csrfToken} />
      {!overview ? <Empty insights={data} /> : (
        <div className="card insights-card">
          <h3 className="doc-label">What it's about</h3>
          <p><Point point={overview.about} /></p>
          <h3 className="doc-label">Where it stands</h3>
          <p><Point point={overview.status} /></p>

          <h3 className="doc-label">Key decisions</h3>
          {overview.decisions.length ? (
            <ul>{overview.decisions.map((d, i) => <li key={i}><Point point={d} /></li>)}</ul>
          ) : <p className="doc-empty">None yet.</p>}

          <h3 className="doc-label">Action items</h3>
          {overview.action_items.length ? (
            <table className="doc-table insights-actions">
              <thead><tr><th>Task</th><th className="owner">Owner</th><th className="due">Due</th></tr></thead>
              <tbody>
                {[...overview.action_items].sort((a, b) => Number(a.done) - Number(b.done)).map((item, i) => (
                  <tr key={i} className={item.done ? "done" : undefined}>
                    <td>
                      {item.done && <span className="insight-done">Done</span>}
                      <span className="insight-task">{item.task}</span>
                      <Cites cites={item.cites} />
                    </td>
                    <td className="owner">{item.owner ?? "—"}</td>
                    <td className="due">{item.due ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="doc-empty">None yet.</p>}

          <h3 className="doc-label">Open questions and risks</h3>
          {overview.open_questions.length ? (
            <ul>{overview.open_questions.map((q, i) => <li key={i}><Point point={q} /></li>)}</ul>
          ) : <p className="doc-empty">Nothing open.</p>}

          <h3 className="doc-label">People</h3>
          <ul className="insights-people">
            {overview.people.map((person) => (
              <li key={person.name}><strong>{person.name}</strong> — {person.role}</li>
            ))}
          </ul>
          <p className="insights-note">Written by AI from the meetings; check anything important against them.</p>
        </div>
      )}
    </section>
  );
}

export function ProjectTimeline({ projectId, csrfToken }: { projectId: number; csrfToken: string }) {
  const { data, error, isLoading } = useInsights(projectId);
  const [mode, setMode] = useState<Mode>(() => {
    try {
      return localStorage.getItem(MODE_KEY) === "meeting" ? "meeting" : "topic";
    } catch {
      return "topic";
    }
  });
  function choose(next: Mode) {
    setMode(next);
    try {
      localStorage.setItem(MODE_KEY, next);
    } catch {
      /* private window: the choice just isn't remembered */
    }
  }
  if (error) return <div className="error">{(error as Error).message}</div>;
  if (isLoading || !data) return <p className="doc-empty">Loading…</p>;
  return (
    <section className="insights">
      <StatusLine projectId={projectId} insights={data} csrfToken={csrfToken} />
      <div className="segmented insights-toggle" role="tablist" aria-label="Timeline">
        <button role="tab" aria-selected={mode === "meeting"} className={mode === "meeting" ? "on" : ""} onClick={() => choose("meeting")}>
          By meeting
        </button>
        <button role="tab" aria-selected={mode === "topic"} className={mode === "topic" ? "on" : ""} onClick={() => choose("topic")}>
          By topic
        </button>
      </div>

      {mode === "meeting" ? (
        data.by_meeting.length === 0 ? <Empty insights={data} /> : (
          <div className="card timeline">
            {data.by_meeting.map((meeting) => (
              <div key={meeting.session_id} className="timeline-event">
                <div className="timeline-date">
                  {shortDate(meeting.date)} · <Link to={`/meetings/${meeting.session_id}`}>{meeting.title}</Link>
                </div>
                {meeting.points.length ? (
                  <ul>
                    {meeting.points.map((point, i) => (
                      <li key={i}>
                        {point.text}
                        {point.seconds != null && (
                          <Link className="cite" to={`/meetings/${meeting.session_id}?t=${point.seconds}`}>{clock(point.seconds)}</Link>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="doc-empty">{meeting.has_minutes ? "Being written…" : "No minutes for this meeting."}</p>
                )}
              </div>
            ))}
          </div>
        )
      ) : !data.threads ? <Empty insights={data} /> : data.threads.length === 0 ? (
        <p className="doc-empty">No topic has come up in more than one meeting yet.</p>
      ) : (
        <div className="card threads">
          {data.threads.map((thread) => (
            <div key={thread.title} className="thread">
              <div className="thread-head">
                <strong>{thread.title}</strong>
                <span className={`thread-state ${thread.state}`}>{thread.state === "settled" ? "Settled" : "Open"}</span>
              </div>
              <ol className="thread-steps">
                {thread.steps.map((step, i) => (
                  <li key={i}>
                    <Link className="thread-when" to={`/meetings/${step.session_id}`} title={step.title}>
                      {shortDate(step.date)} · {step.title}
                    </Link>
                    <span>
                      {step.text}
                      {step.seconds != null && (
                        <Link className="cite" to={`/meetings/${step.session_id}?t=${step.seconds}`}>{clock(step.seconds)}</Link>
                      )}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/** "3 Oct", with the year when it isn't this one. */
function shortDate(value: string | null): string {
  if (!value) return "—";
  const day = new Date(value);
  return day.toLocaleDateString(undefined, {
    day: "numeric", month: "short", ...(day.getFullYear() === new Date().getFullYear() ? {} : { year: "numeric" }),
  });
}

function when(value: string): string {
  const moment = new Date(value);
  return `${moment.toLocaleDateString(undefined, { day: "numeric", month: "short" })}, ${moment.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
}
